/**
 * Morgenreport — die gemeinsamen Formen und reinen Regeln. (19.10.2026, Paket 1)
 *
 * Martin, 19.10. 07:00: „Morgenreport einmal früh: was jeder einzelne getan hat, was sich Neues getan hat, Wünsche und Beschwerden,
 * Ziel des Tages — jeden Tag um 7 Uhr als Karte im Chat, nur vorgelesen wenn ich sie antippe — und im Adminbereich als Protokoll.“
 *
 * ⚠️ PRIVATES UND GESPRÄCHE NIE (Designregel, server/morgenreport.ts hält sie ein):
 *    Der Report entsteht nur aus ZAHLEN und KURZTITELN aus dem eigenen Haus — Commit-Betreffzeilen, Ticket-Titel (Nummer + Titel),
 *    Titel und Marke der Selbstverbesserung, Versionen, Zähler. Nie ein Text aus einem Gespräch, nie ein Beitragstext der
 *    Entwickler, nie ein Wort von Martin aus dem Chat. Jede Textzeile geht durch den Geheimnis-Filter (schwaerzeText), den
 *    Zeichenfilter (wie pruefeInhalt der Aufträge), den Privat-Filter (nenntPrivates → die Zeile fällt ganz heraus) und eine
 *    harte Längengrenze. Keine KI in Paket 1.
 */

/** Ein Ticket nur mit Nummer und Titel. */
export interface TicketKurz {
  nr: number;
  titel: string;
}

export interface TeamZahlen {
  /** Beiträge im Dreier-Chat der letzten 24 h (nur Zahl). */
  beitraege24h: number | null;
  /** Zeitpunkt des letzten Beitrags in diesen 24 h (ms), sonst null. */
  letzterBeitragMs: number | null;
  /** Hört der Hörer gerade mit? */
  hoertMit: boolean | null;
  /** Sekunden seit dem letzten Lebenszeichen; null: seit dem Start nie. */
  zuletztGesehenS: number | null;
}

export interface MorgenInhalt {
  version: 1;
  tag: string;
  erzeugtMs: number;
  vespera: { zuege24h: number | null; gespraeche24h: number | null; werkzeugLaeufe24h: number | null; fehler24h: number | null };
  schmied: { commits: string[]; commitsGekuerzt: number; version: string | null; erledigt: TicketKurz[] };
  team: { zimmermann: TeamZahlen; hueter: TeamZahlen };
  neues: { claudeCode: string; server: string | null };
  wuensche: { vorschlaege: Array<{ titel: string; marke: string }>; rueckmeldungen: number | null };
  ziel: { inArbeit: TicketKurz[]; wartendeFragen: number | null };
  /** Quellen, die ausgefallen sind (Namen aus dem eigenen Haus). */
  ausgefallen: string[];
  /** Die fünf Kurzzeilen der Karte (je Block eine). */
  kurz: string[];
}

/** Grenzen je Block. */
export const GRENZEN = {
  zeile: 120,
  commits: 15,
  tickets: 10,
  vorschlaege: 8,
  ziel: 5,
  kurzZeile: 160,
} as const;

// eslint-disable-next-line no-control-regex
const VERBOTEN_ZEICHEN = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u{E0000}-\u{E007F}]|\p{Cf}|\p{Co}|\p{Cn}/u;

/** Hat der Text Zeichen, die unsichtbar oder umgedreht wirken (wie die Aufträge an Vespera)? Rein. */
export function hatVerbotenesZeichen(text: string): boolean {
  return VERBOTEN_ZEICHEN.test(text);
}

/** Die Datumszeile „19.10.2026“ aus „2026-10-19“. */
export function tagAnzeige(tag: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(tag);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : tag;
}

/** Der Vorlesetext der Karte: nur die Kurzzeilen. Leer ohne Zeilen. */
export function morgenVorlesetext(tag: string, kurz: readonly string[]): string {
  const zeilen = kurz
    .map((z) => z.trim())
    .filter(Boolean)
    .map((z) => (/[.!?…:]$/u.test(z) ? z : `${z}.`));
  return zeilen.length === 0 ? "" : `Morgenreport ${tagAnzeige(tag)}. ${zeilen.join(" ")}`;
}

/** Was die Karte zeigt (Antwort von GET /api/morgenreport/karte). */
export interface KartenStand {
  tag: string;
  kurz: string[];
  gehoert: boolean;
  /** Martin hat den Report geöffnet oder als gelesen markiert (serverseitig, wie geschlossen_ms): kein Punkt am Chip mehr. */
  gelesen: boolean;
}

/** Liest die Karten-Antwort streng; alles andere: null. Rein. */
export function leseKartenStand(roh: unknown): KartenStand | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  if (typeof r.tag !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(r.tag)) return null;
  if (!Array.isArray(r.kurz)) return null;
  const kurz = r.kurz.filter((z): z is string => typeof z === "string").slice(0, 5);
  if (kurz.length === 0) return null;
  return { tag: r.tag, kurz, gehoert: r.gehoert === true, gelesen: r.gelesen === true };
}

export const KARTE_PFAD = "/api/morgenreport/karte";
export const SCHLIESSEN_PFAD = "/api/morgenreport/schliessen";
export const GEHOERT_PFAD = "/api/morgenreport/gehoert";
/** Der Seiten-Hash der In-App-Seite „Morgenreport“ (frontend/lib/morgenreport-seite.ts); Ziel von „Alle Einzelheiten“ in der Chat-Karte. */
export const SEITE_HASH = "morgenreport";
export const ADMIN_SEITE = "/admin/morgenreport";
/** Martin tippt „Ziel mit Schmied besprechen“: app.ts legt den Text ins Eingabefeld (An: Schmied). Martin sendet selbst. */
export const ZIEL_BESPRECHEN_EREIGNIS = "voice-v8:ziel-besprechen";
/** Der Report wurde als gelesen markiert: der Chip im Chat nimmt seinen Punkt weg. */
export const GELESEN_EREIGNIS = "voice-v8:morgenreport-gelesen";
const ENTWURF_MAX = 600;

/** Der neue Feldinhalt: ein angefangener Entwurf bleibt stehen, der Zielsatz wird angehängt (nie überschrieben); schon enthalten: unverändert. Rein. */
export function entwurfMitZiel(aktuell: string, ziel: string): string {
  const alt = aktuell.trim();
  if (!alt) return ziel.slice(0, ENTWURF_MAX);
  if (alt.includes(ziel)) return aktuell;
  return `${alt}\n\n${ziel}`.slice(0, ENTWURF_MAX * 3);
}
